import { useEffect, useState } from 'react'
import { authFetch } from '@/auth/authFetch'
import { API_BASE_URL } from '@/analysis/endpoints'
import { useHasPermission } from '@/auth/hooks'

type AdminStatus = {
  env?: string
  version?: string
  kafka?: {
    brokersConfigured?: boolean
    topics?: Record<string, string | null>
  }
  amplify?: {
    allowed?: boolean
    reason?: string
    mode?: string
    recording?: boolean
    error?: string
    message?: string
  } | null
  notifier?: { slackConfigured?: boolean }
  incidentsCached?: number
}

type UserSettings = {
  userId?: string
  defaultView?: string | null
  defaultGrain?: string | null
  timezone?: string | null
  notificationsMuted?: boolean
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <tr>
      <td style={{ color: 'var(--t-2)', width: 180, padding: '8px 10px' }}>{label}</td>
      <td style={{ color: 'var(--t-0)', padding: '8px 10px', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
        {value}
      </td>
    </tr>
  )
}

export default function SettingsView() {
  const isAdmin = useHasPermission('user:manage')
  const [status, setStatus] = useState<AdminStatus | null>(null)
  const [adminError, setAdminError] = useState<string | null>(null)
  const [prefs, setPrefs] = useState<UserSettings>({
    defaultView: 'world',
    defaultGrain: 'city',
    timezone: '',
    notificationsMuted: false,
  })
  const [prefsMsg, setPrefsMsg] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const res = await authFetch(`${API_BASE_URL}/api/settings/me`)
        if (!res.ok) return
        const body = await res.json() as { settings?: UserSettings }
        if (!cancelled && body.settings) {
          setPrefs({
            defaultView: body.settings.defaultView ?? 'world',
            defaultGrain: body.settings.defaultGrain ?? 'city',
            timezone: body.settings.timezone ?? '',
            notificationsMuted: Boolean(body.settings.notificationsMuted),
          })
        }
      } catch {
        /* defaults remain */
      }
    })()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (!isAdmin) return
    let cancelled = false
    void (async () => {
      try {
        const res = await authFetch(`${API_BASE_URL}/api/admin/status`)
        if (!res.ok) {
          const body = await res.json().catch(() => ({})) as { error?: string; message?: string }
          throw new Error(body.message || body.error || `HTTP ${res.status}`)
        }
        const body = await res.json() as AdminStatus
        if (!cancelled) setStatus(body)
      } catch (e) {
        if (!cancelled) setAdminError(String((e as Error)?.message ?? e))
      }
    })()
    return () => { cancelled = true }
  }, [isAdmin])

  const savePrefs = async () => {
    setSaving(true)
    setPrefsMsg(null)
    try {
      const res = await authFetch(`${API_BASE_URL}/api/settings/me`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          defaultView: prefs.defaultView || 'world',
          defaultGrain: prefs.defaultGrain || 'city',
          timezone: prefs.timezone || null,
          notificationsMuted: Boolean(prefs.notificationsMuted),
        }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const body = await res.json() as { settings?: UserSettings }
      if (body.settings) {
        setPrefs({
          defaultView: body.settings.defaultView ?? 'world',
          defaultGrain: body.settings.defaultGrain ?? 'city',
          timezone: body.settings.timezone ?? '',
          notificationsMuted: Boolean(body.settings.notificationsMuted),
        })
      }
      setPrefsMsg('Saved')
    } catch (e) {
      setPrefsMsg(String((e as Error)?.message ?? e))
    } finally {
      setSaving(false)
    }
  }

  const amp = status?.amplify
  const ampLabel = amp?.error
    ? `unreachable (${amp.message ?? amp.error})`
    : amp?.allowed === false
      ? `denied (${amp.reason ?? 'gate'})`
      : amp?.allowed
        ? `allowed${amp.mode ? ` · ${amp.mode}` : ''}${amp.recording ? ' · recording' : ''}`
        : '—'

  const fieldStyle: React.CSSProperties = {
    display: 'block',
    width: '100%',
    maxWidth: 320,
    marginTop: 4,
    padding: '6px 8px',
    fontSize: 12,
    background: 'var(--bg-2)',
    border: '0.5px solid var(--bd-0)',
    borderRadius: 'var(--radius)',
    color: 'var(--t-0)',
    boxSizing: 'border-box',
  }

  return (
    <div className="panel" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div className="panel-head">
        <span className="panel-title">Settings</span>
      </div>
      <div style={{ overflow: 'auto', flex: 1, padding: 12, display: 'flex', flexDirection: 'column', gap: 20 }}>
        <section>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--t-1)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8 }}>
            Your preferences
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 360 }}>
            <label style={{ fontSize: 12, color: 'var(--t-2)' }}>
              Default view
              <select
                value={prefs.defaultView ?? 'world'}
                onChange={(e) => setPrefs((p) => ({ ...p, defaultView: e.target.value }))}
                style={fieldStyle}
              >
                <option value="world">World</option>
                <option value="hex">Hex</option>
                <option value="deep">Deep dive</option>
              </select>
            </label>
            <label style={{ fontSize: 12, color: 'var(--t-2)' }}>
              Default client grain
              <select
                value={prefs.defaultGrain ?? 'city'}
                onChange={(e) => setPrefs((p) => ({ ...p, defaultGrain: e.target.value }))}
                style={fieldStyle}
              >
                <option value="continent">Continent</option>
                <option value="country">Country</option>
                <option value="city">City</option>
              </select>
            </label>
            <label style={{ fontSize: 12, color: 'var(--t-2)' }}>
              Timezone
              <input
                value={prefs.timezone ?? ''}
                onChange={(e) => setPrefs((p) => ({ ...p, timezone: e.target.value }))}
                placeholder="e.g. Europe/London"
                style={fieldStyle}
              />
            </label>
            <label style={{ fontSize: 12, color: 'var(--t-1)', display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={Boolean(prefs.notificationsMuted)}
                onChange={(e) => setPrefs((p) => ({ ...p, notificationsMuted: e.target.checked }))}
              />
              Mute notifications
            </label>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <button
                type="button"
                className="chip"
                disabled={saving}
                onClick={() => void savePrefs()}
                style={{ fontSize: 11, cursor: 'pointer', padding: '6px 12px' }}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              {prefsMsg && <span style={{ fontSize: 11, color: 'var(--t-2)' }}>{prefsMsg}</span>}
            </div>
          </div>
        </section>

        {isAdmin && (
          <section>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--t-1)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8 }}>
              Pipeline / admin
            </div>
            {adminError && (
              <div style={{ color: 'var(--crit)', fontSize: 12, marginBottom: 12 }}>{adminError}</div>
            )}
            {!status && !adminError && (
              <div style={{ color: 'var(--t-2)', fontSize: 12 }}>Loading…</div>
            )}
            {status && (
              <table className="vms-table" style={{ maxWidth: 720 }}>
                <tbody>
                  <Row label="Environment" value={status.env ?? '—'} />
                  <Row label="Version" value={status.version ?? '—'} />
                  <Row label="Amplify gate" value={ampLabel} />
                  <Row
                    label="Slack notifier"
                    value={status.notifier?.slackConfigured ? 'configured' : 'not configured (log-only)'}
                  />
                  <Row label="Kafka brokers" value={status.kafka?.brokersConfigured ? 'yes' : 'no'} />
                  <Row label="Topic · streams" value={status.kafka?.topics?.streams ?? '—'} />
                  <Row label="Topic · world" value={status.kafka?.topics?.world ?? '—'} />
                  <Row label="Topic · events" value={status.kafka?.topics?.incidentEvents ?? '—'} />
                  <Row label="Topic · signals" value={status.kafka?.topics?.signals ?? '—'} />
                  <Row label="Incidents cached" value={String(status.incidentsCached ?? 0)} />
                </tbody>
              </table>
            )}
          </section>
        )}
      </div>
    </div>
  )
}
